import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Badge, Button, Collapse, Table } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import {
  faChevronDown,
  faChevronUp,
  faEdit,
  faPlay,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import Swal from 'sweetalert2';

import { getJobRunStatusVariant } from 'lib/enums/JobRunStatus';
import { JOBS, JOB_RUNS } from 'lib/networking/endpoints';
import NewJobModal from 'components/jobs/NewJobModal';
import RunJobModal from 'components/jobs/RunJobModal';
import { SwalToast } from 'lib/utils/toast';
import useDelete from 'lib/hooks/useDelete';
import useGet from 'lib/hooks/useGet';
import UserPermission from 'lib/enums/UserPermission';
import useUserPermissions from 'lib/hooks/useUserPermissions';

dayjs.extend(relativeTime);

// A job returns whatever dict it likes as run metadata, so this reads the shape rather than
// assuming one: top-level entries one per line, an object flattened onto its key, an array
// joined. Anything deeper is stringified rather than rendered as [object Object]. Without this
// a finished run shows a green badge and nothing about what it found.
export function summariseMetadata(metadata) {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
    return null;
  }
  const entries = Object.entries(metadata)
    .filter(([, value]) => !isEmpty(value))
    .map(([key, value]) => ({ key, text: describe(value) }));
  return entries.length ? entries : null;
}

function describe(value) {
  if (Array.isArray(value)) {
    return value.map(describe).join(', ');
  }
  if (value && typeof value === 'object') {
    return Object.entries(value)
      .map(([key, inner]) => `${key}: ${describe(inner)}`)
      .join(', ');
  }
  return String(value);
}

function isEmpty(value) {
  if (value === null || value === undefined || value === '') {
    return true;
  }
  return typeof value === 'object' && !Object.keys(value).length;
}

function JobTableRow({ job, refetch }) {
  const [expanded, setExpanded] = useState(false);
  const [showRunModal, setShowRunModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const hasUserPermissions = useUserPermissions();
  const canManage = hasUserPermissions([UserPermission.MANAGE_JOBS]);

  const { deleteData: deleteJob } = useDelete(
    `${JOBS}/${job.id}`,
    false,
    () => {
      SwalToast('success', `"${job.name}" deleted successfully`);
      refetch();
    },
    () => SwalToast('error', `Could not delete "${job.name}". Please try again.`),
  );

  const { data: { data: runs = [] } = {}, refetch: refetchRuns } = useGet(
    JOB_RUNS(job.id),
    { limit: 10 },
    !expanded,
  );

  const handleDelete = () => {
    Swal.fire({
      title: 'Delete Job',
      text: `Are you sure you want to delete "${job.name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#d33',
    }).then(result => {
      if (result.isConfirmed) {
        deleteJob({});
      }
    });
  };

  const lastRun = runs[0];

  return (
    <>
      <tr className={css(!job.enabled && styles.disabledRow)}>
        <td>{job.name}</td>
        <td>
          <small className="text-muted">
            {job.module_name}.{job.class_name}.{job.method}
          </small>
        </td>
        <td>
          {job.cron_expression ? (
            <>
              <Badge variant="info" className="mr-1">
                Scheduled
              </Badge>
              <small>{job.cron_expression}</small>
              {job.get_human_readable_next_run && (
                <div>
                  <small className="text-muted">Next: {job.get_human_readable_next_run}</small>
                </div>
              )}
            </>
          ) : (
            <small className="text-muted">Manual only</small>
          )}
        </td>
        <td>
          {lastRun ? (
            <Badge variant={getJobRunStatusVariant(lastRun.status)}>{lastRun.status}</Badge>
          ) : (
            <small className="text-muted">No runs</small>
          )}
        </td>
        <td>
          <Button
            variant="success"
            size="sm"
            className="mr-1"
            disabled={!job.enabled}
            onClick={() => setShowRunModal(true)}
          >
            <FontAwesomeIcon icon={faPlay} className="mr-1" />
            Run
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            className="mr-1"
            onClick={() => {
              setExpanded(prev => !prev);
              if (!expanded) refetchRuns();
            }}
          >
            <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} />
          </Button>
          {canManage && (
            <>
              <Button
                variant="outline-primary"
                size="sm"
                className="mr-1"
                onClick={() => setShowEditModal(true)}
              >
                <FontAwesomeIcon icon={faEdit} />
              </Button>
              <Button variant="outline-danger" size="sm" onClick={handleDelete}>
                <FontAwesomeIcon icon={faTrash} />
              </Button>
            </>
          )}
        </td>
      </tr>
      <tr>
        <td colSpan={5} className="p-0">
          <Collapse in={expanded}>
            <div className={css(styles.runsContainer)}>
              <Table size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Run ID</th>
                    <th>Status</th>
                    <th>Started</th>
                    <th>Completed</th>
                    <th>Result</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map(run => {
                    const metadata = summariseMetadata(run.result_metadata);
                    return (
                      <tr key={run.id}>
                        <td>#{run.id}</td>
                        <td>
                          <Badge variant={getJobRunStatusVariant(run.status)}>{run.status}</Badge>
                        </td>
                        <td>
                          {run.started_at ? (
                            dayjs(run.started_at).fromNow()
                          ) : (
                            <small className="text-muted">—</small>
                          )}
                        </td>
                        <td>
                          {run.completed_at ? (
                            dayjs(run.completed_at).fromNow()
                          ) : (
                            <small className="text-muted">—</small>
                          )}
                        </td>
                        <td>
                          {metadata ? (
                            metadata.map(entry => (
                              <div key={entry.key}>
                                <small className="text-muted">{entry.key}:</small>{' '}
                                <small className={css(styles.metadataValue)}>{entry.text}</small>
                              </div>
                            ))
                          ) : (
                            <small className="text-muted">—</small>
                          )}
                        </td>
                        <td>
                          {run.error_message ? (
                            <small className="text-danger">{run.error_message.slice(0, 80)}</small>
                          ) : (
                            <small className="text-muted">—</small>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {runs.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center text-muted">
                        No runs yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Collapse>
        </td>
      </tr>
      {showRunModal && (
        <RunJobModal
          job={job}
          onHide={() => setShowRunModal(false)}
          refetch={() => {
            refetch();
            if (expanded) refetchRuns();
          }}
        />
      )}
      {showEditModal && (
        <NewJobModal job={job} onHide={() => setShowEditModal(false)} refetch={refetch} />
      )}
    </>
  );
}

JobTableRow.propTypes = {
  job: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    module_name: PropTypes.string,
    class_name: PropTypes.string,
    method: PropTypes.string,
    enabled: PropTypes.bool,
    cron_expression: PropTypes.string,
    get_human_readable_next_run: PropTypes.string,
  }).isRequired,
  refetch: PropTypes.func.isRequired,
};

const styles = StyleSheet.create({
  disabledRow: {
    opacity: 0.5,
  },
  runsContainer: {
    backgroundColor: '#f8f9fa',
    padding: '8px',
  },
  metadataValue: {
    fontFamily: 'monospace',
    wordBreak: 'break-word',
  },
});

export default JobTableRow;
